<template>
  <z-table
    ref="groupableTable"
    show-select
    :headers="computedHeaders"
    v-bind="$props"
    v-on="$listeners"
  >
    <template v-for="(_, name) in $scopedSlots" v-slot:[name]="data">
      <slot :name="name" v-bind="data"></slot>
    </template>

    <template #top>
      <div class="top-table">
        <div class="content">
          <v-slide-x-transition>
            <div v-if="isGroupColumnsActive" class="buttons-container">
              <z-btn
                small
                text="cancelar"
                @click="isGroupColumnsActive = null"
              ></z-btn>

              <z-btn
                small
                primary
                text="agrupar"
                :disabled="!selectedHeaders.length"
                @click="onGroupColumns"
              ></z-btn>
            </div>
          </v-slide-x-transition>

          <div>
            <v-menu
              max-width="290px"
              min-width="auto"
              offset-y
              left
              content-class="mt-2"
            >
              <template #activator="{on, attrs}">
                <z-icon v-bind="attrs" v-on="on">$more_vert_rounded</z-icon>
              </template>

              <v-list dense>
                <v-list-item-group
                  v-model="isGroupColumnsActive"
                  color="primary"
                >
                  <v-list-item value="active">
                    <v-list-item-content>
                      <v-list-item-title>Agrupar Colunas</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>

      <v-divider></v-divider>
    </template>

    <template v-if="isGroupColumnsActive" #header.data-table-select>
      <z-tooltip top>
        <template #activator="{on, attrs}">
          <z-icon
            v-ripple
            style="cursor: pointer"
            v-bind="attrs"
            v-on="on"
            @click="toggleSelectedHeaders"
          >
            {{
              selectedHeaders.length
                ? '$check_box_rounded'
                : '$check_box_outline_blank_rounded'
            }}
          </z-icon>
        </template>

        {{ selectedHeaders.length ? 'Desmarcar todos' : 'Selecionar todos' }}
      </z-tooltip>
    </template>

    <template
      v-for="(header, index) in computedHeaders"
      v-slot:[`header.${header.value}`]="headerValue"
    >
      <div
        v-if="header.type === 'group' && groupedColumns.length && header.value"
        :key="index"
        class="group-divider__wrapper"
      >
        <div class="group-divider" :style="{height: `${groupDividerHeight}px`}">
          <div class="child">
            <v-menu
              max-width="290px"
              min-width="auto"
              offset-x
              content-class="ml-2"
            >
              <template #activator="{on, attrs}">
                <z-icon class="icon" v-bind="attrs" v-on="on">
                  $more_vert_rounded
                </z-icon>
              </template>

              <v-list dense>
                <v-list-item
                  style="cursor: pointer"
                  ripple
                  @click="onToggleColumn(header)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{
                        expandedGroups.includes(header.value)
                          ? 'Minimizar'
                          : 'Expandir'
                      }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item
                  style="cursor: pointer"
                  ripple
                  @click="onUngroupColumns(header, index)"
                >
                  <v-list-item-content>
                    <v-list-item-title>Desagrupar Colunas</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>

      <div
        v-if="header.type === 'group' && groupedColumns.length && !header.value"
        :key="index + Math.random()"
        class="group-divider__wrapper"
      >
        <div
          class="group-divider"
          :style="{height: `${groupDividerHeight}px`}"
        ></div>
      </div>

      <v-slide-x-transition :key="index + Math.random()" hide-on-leave>
        <div v-if="header.type !== 'group'" class="d-flex align-center">
          <v-slide-x-transition hide-on-leave>
            <z-icon
              v-if="isGroupColumnsActive && !isHeaderGrouped(header)"
              class="mr-5"
              style="cursor: pointer"
              @click="onSelectHeader(header)"
            >
              {{
                isHeaderSelected(header)
                  ? '$check_box_rounded'
                  : '$check_box_outline_blank_rounded'
              }}
            </z-icon>
          </v-slide-x-transition>
          <div class="d-flex mr-5">
            {{ headerValue.text }}
            <v-icon v-if="header.sortable" class="ml-1" small>
              $unfold_more_rounded
            </v-icon>
          </div>

          <template v-if="$scopedSlots['header-filter']">
            <v-menu
              max-width="290px"
              min-width="auto"
              offset-y
              bottom
              left
              content-class="mt-2"
              v-bind="filterMenuProps"
              :close-on-content-click="closeOnFilterContentClick"
            >
              <template #activator="{on, attrs}">
                <z-icon style="cursor: pointer" v-bind="attrs" v-on="on">
                  $filter_list_rounded
                </z-icon>
              </template>

              <slot name="header-filter" v-bind="header">
                <z-simple-filter
                  @cancel="onCancelFilter"
                  @save="onSaveFilter({header, filter: $event})"
                ></z-simple-filter>
              </slot>
            </v-menu>
          </template>
        </div>
      </v-slide-x-transition>
    </template>

    <template #item.data-table-select> </template>
  </z-table>
</template>

<script>
export default {
  name: 'ZGroupableTable',
  props: {
    expandable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Flag para indicar que o Header não deve ser exibido
     */
    hiddenHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Headers da tabela
     *
     * @typedef {Object} {text:string:value:any,sortable:boolean}
     */
    headers: {
      type: Array,
      required: true,
    },
    /**
     * Itens da lista
     */
    items: {
      type: Array,
      required: true,
    },

    /**
     * Flag para fazer paginação automática
     */
    autoPagination: {
      type: Boolean,
      default: false,
    },
    /**
     * Quantide de itens por página (autoPagination)
     */
    itemsPerPage: {
      type: Number,
      required: false,
    },
    /**
     * Define a página atual (ignorado com autopaging)
     */
    currentPage: {
      type: Number,
      required: false,
    },
    /**
     * Define a página atual (ignorado com autopaging)
     */
    numberOfPages: {
      type: Number,
      required: false,
    },
    /**
     * Flag para indicar ao componente o ícone que irá passar as páginas para direita.
     */
    iconNext: {
      type: String,
      default: '$keyboard_arrow_right_rounded',
    },
    /**
     * Flag para indicar ao componente o ícone que irá passar as páginas para esquerda.
     */
    iconPrevious: {
      type: String,
      default: '$keyboard_arrow_left_rounded',
    },

    /**
     * Aplica estilo de loading na tabela
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar que a tabela deve ser pequena
     */
    small: {
      type: Boolean,
      default: false,
    },
    singleExpand: {
      type: Boolean,
      default: false,
    },
    /**
     * Repassa as props  para o v-menu usado no slot de filtro
     */
    filterMenuProps: {
      type: Object,
      default: () => ({
        closeOnContentClick: false,
      }),
    },
  },

  data() {
    return {
      isGroupColumnsActive: null,
      selectedHeaders: [],
      groupedColumns: [],
      expandedGroups: [],

      groupDividerHeight: 0,

      closeOnFilterContentClick: false,
    }
  },

  computed: {
    computedHeaders() {
      const groupedHeaders = this.groupedColumns.flat()
      const firstItemByGroup = this.groupedColumns.map((group) => group[0])
      const headers = []

      for (let i = 0; i < this.headers.length; i++) {
        const header = this.headers[i]
        if (groupedHeaders.includes(header)) {
          if (firstItemByGroup.includes(header)) {
            let groupedHeader = this.groupedColumns.filter((arr) =>
              arr.includes(header)
            )[0]

            let result = {
              type: 'group',
              group: groupedHeader,
              value: `group-${i}`,
            }

            if (this.expandedGroups.includes(result.value)) {
              headers.push(result)
              headers.push(...result.group)
              headers.push({type: 'group'})
            } else {
              headers.push(result)
            }
          }
        } else {
          headers.push(header)
        }
      }

      return headers
    },
  },

  watch: {
    isGroupColumnsActive(newValue) {
      if (!newValue) {
        this.selectedHeaders = []
      }
    },
  },

  mounted() {
    this.groupDividerHeight = this.$refs.groupableTable.$el.querySelector(
      '.v-data-table__wrapper'
    ).offsetHeight
  },

  methods: {
    onSelectHeader(header) {
      if (this.isHeaderSelected(header)) {
        this.selectedHeaders = this.selectedHeaders.filter(
          (item) => item !== header
        )
      } else {
        this.selectedHeaders.push(header)
      }
    },

    toggleSelectedHeaders() {
      if (this.selectedHeaders.length > 0) {
        this.selectedHeaders = []
      } else {
        // https://stackoverflow.com/a/44204227
        let grouped = new Set(this.groupedColumns.flat())
        this.selectedHeaders = this.headers.filter((item) => !grouped.has(item))
      }
    },

    isHeaderSelected(header) {
      return this.selectedHeaders.includes(header)
    },

    onGroupColumns() {
      this.groupedColumns.push([...this.selectedHeaders])

      this.selectedHeaders = []
      this.isGroupColumnsActive = null
    },

    onToggleColumn({value}) {
      if (this.expandedGroups.includes(value)) {
        this.expandedGroups = this.expandedGroups.filter(
          (expandedColumn) => expandedColumn !== value
        )
      } else {
        this.expandedGroups.push(value)
      }
    },

    onUngroupColumns({group}) {
      let indexOfGroup = this.groupedColumns.indexOf(group)

      this.groupedColumns.splice(indexOfGroup, 1)
    },

    isHeaderGrouped(header) {
      return this.groupedColumns.flat().includes(header)
    },

    onSaveFilter({header, filter}) {
      this.$emit('filter:save', {
        headerValue: header.value,
        selectedFilter: filter.filter,
        filterValue: filter.valueToFilter,
      })

      this.closeOnFilterContentClick = true

      setTimeout(() => {
        this.closeOnFilterContentClick = false
      }, 100)
    },

    onCancelFilter() {
      this.$emit('filter:cancel')

      this.closeOnFilterContentClick = true

      setTimeout(() => {
        this.closeOnFilterContentClick = false
      }, 100)
    },
  },
}
</script>

<style lang="scss" scoped>
.top-table {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: 10px;
  height: 50px;

  .content {
    display: flex;
    align-items: center;
    gap: 20px;
  }
}

.group-divider__wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.group-divider {
  position: absolute;
  top: 0;
  background: var(--black-600);
  width: 2px;

  .child {
    background: white;
    width: 2px;
    height: 25px;
    position: relative;
    top: 10%;
    cursor: pointer;

    .icon {
      position: absolute;
      left: -11px;
      top: 0;
    }
  }
}
</style>
